import React, { useState, useEffect } from "react";
import GYLogo from "../../../images/BASE64/GYLogo";
import { useSolarCalc } from "../../../util/calculations";
import { useFormatter, numToWordsBaht } from "../../../util/formatter";
import fonts_config from "./fonts_config.json";
import pdfFonts from "pdfmake/build/vfs_fonts.js";
import pdfMake from "pdfmake/build/pdfmake.js";

import moment from "moment-timezone";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfMake.vfs;
pdfMake.fonts = fonts_config;

const ReceiptTemplate = (props) => {
	const [formatNumber] = useFormatter();
	const solarCalc = useSolarCalc();
	const parseNo = solarCalc.parseFormatNumber;
	const [PDF, setPDF] = useState(null);
	const logo = GYLogo;

	const previewStyle = {
		width: "100%",
		height: "72vh",
	};

	/**
	 * Generate Receipt PDF
	 * @param {obj} dataObj
	 */
	const generateReceipt = (dataObj) => {
		const invoiceData = !Array.isArray(dataObj.invoice)
			? dataObj.invoice
			: dataObj.invoice[0];
		const receiptData = dataObj.receipt;

		const isBTGSite = invoiceData.client_id == 69;
		const specialDP = isBTGSite ? 4 : null;

		const isMakroReceipt = dataObj.isMakroReceipt;
		const invoiceNo = receiptData.invoiceNo;
		const billingName =
			invoiceData.billing_name != null ? invoiceData.billing_name : "-";
		const billingAddress =
			invoiceData.billing_address != null ? invoiceData.billing_address : "-";
		const paymentDate =
			receiptData.paymentDate != null
				? moment(receiptData.paymentDate).format("DD/MM/YYYY")
				: "-";
		const customerID = !Array.isArray(dataObj.invoice)
			? invoiceData.customer_id
			: invoiceData.company;
		const solarEntity = {
			name: invoiceData.entity_name,
			taxID: invoiceData.tax_id,
			entityID: invoiceData.entity_id,
			address: invoiceData.entity_address,
			accountantName: invoiceData.contact_name,
			telephone: invoiceData.telephone,
			email: invoiceData.email,
		};
		const tempEntityName = solarEntity.name;
		const authName = tempEntityName.replace(" (Head Office)", "");

		const dateStr = invoiceNo.slice(-7, -3)
		const modifiedStr = "01-" + dateStr.slice(2, 4) + "-20" + dateStr.slice(0, 2);
		const selectedDate = moment(modifiedStr).format("DD/MM/YYYY")
		const _cod = moment(invoiceData.cod);
		const _startMonth = moment(selectedDate).startOf("month");
		const _endMonth = moment(selectedDate).endOf("month");
		const _msaStartMonth = _cod.diff(_startMonth, "years", true);
		const period = `${_msaStartMonth < 0 ? moment(_startMonth).format("DD/MM/YYYY") : moment(_cod).format("DD/MM/YYYY")} - ${moment(_endMonth).format("DD/MM/YYYY")}`;

		let tableContents = {};
		let subtotalPrice = 0;
		let VATPrice = 0;
		const vatDisplay = parseNo(invoiceData.vat * 100);
		let totalDue = 0;

		// Makro invoice (Multiple billing details)
		if (invoiceNo.includes("MKO")) {
			totalDue = receiptData.totalDue;
			subtotalPrice = receiptData.subtotalPrice;
			VATPrice = receiptData.VATPrice;
		}
		// Regular invoices
		else {
			// Solar Production values
			const solarON = formatNumber(invoiceData.solar_ON, 3);
			const solarOFF = formatNumber(invoiceData.solar_OFF, 3);
			const solarTotal = formatNumber(invoiceData.solar_total, 3);

			// Discount text
			const discountON_display =
				invoiceData.ON_discount > 0
					? getDiscountText(invoiceData.ON_discount)
					: "";
			const discountOFF_display =
				invoiceData.OFF_discount > 0
					? getDiscountText(invoiceData.OFF_discount)
					: "";
			const discountFT_display =
				invoiceData.FT_discount > 0
					? getDiscountText(invoiceData.FT_discount)
					: "";

			// Discounted Price
			const priceON_disc = solarCalc.calculateUnitCost(
				invoiceData.price_on,
				invoiceData.ON_discount,
				invoiceData.currency,
				specialDP
			);
			const priceOFF_disc = solarCalc.calculateUnitCost(
				invoiceData.price_off,
				invoiceData.OFF_discount,
				invoiceData.currency,
				specialDP
			);
			const FTcost_disc = solarCalc.calculateUnitCost(
				invoiceData.FT_cost,
				invoiceData.FT_discount,
				invoiceData.currency,
				specialDP
			);

			// Cost calculations
			const prod = {
				ON: invoiceData.solar_ON,
				OFF: invoiceData.solar_OFF,
				TOTAL: invoiceData.solar_total,
			};
			const price = {
				ON: invoiceData.price_on,
				OFF: invoiceData.price_off,
				FT: invoiceData.FT_cost,
			};
			const discount = {
				ON: invoiceData.ON_discount,
				OFF: invoiceData.OFF_discount,
				FT: invoiceData.FT_discount,
			};

			// Cost calculations results
			const costsObj = solarCalc.calculateProdCost(
				prod,
				price,
				discount,
				invoiceData.currency,
				specialDP
			);

			// Prices
			const totalPriceON = costsObj.costON;
			const totalPriceOFF = costsObj.costOFF;
			const totalPriceFT = costsObj.costFT;

			// Subtotal price
			subtotalPrice = costsObj.costTotal;

			// Only for sites with BPA fee
			if (invoiceData.BPA_fee > 0)
				subtotalPrice = subtotalPrice + invoiceData.BPA_fee;

			// VAT calculations
			const currency = invoiceData.currency;
			const vatPercent = invoiceData.vat;

			const VATObj = solarCalc.calculateVAT(
				subtotalPrice,
				currency,
				vatPercent
			);

			VATPrice = VATObj.VAT;
			const tempTotal = VATObj.totalDue;

			let discountPrice = 0;
			let specialDiscount_display = "";

			// Special discounts
			if (invoiceData.special_discount === null) totalDue = tempTotal;
			else {
				const specialDiscount = formatNumber(
					invoiceData.special_discount * 100,
					1
				);
				specialDiscount_display = `Discount ${specialDiscount}%`;
				discountPrice = -(tempTotal * invoiceData.special_discount);

				totalDue =
					parseFloat(subtotalPrice.toFixed(2)) +
					parseFloat(VATPrice.toFixed(2)) +
					parseFloat(discountPrice.toFixed(2));
			}

			tableContents = {
				discountFT_display,
				discountFT_display,
				discountOFF_display,
				discountON_display,
				FTcost_disc,
				FTcost_disc,
				priceOFF_disc,
				priceON_disc,
				solarOFF,
				solarON,
				solarTotal,
				solarTotal,
				subtotalPrice,
				tempTotal,
				totalPriceFT,
				totalPriceFT,
				totalPriceOFF,
				totalPriceON,
				VATPrice,
			};
		}

		const totalDueWords = numToWordsBaht(totalDue.toFixed(2)).toUpperCase();

		const tableParam = {
			...invoiceData,
			...tableContents,
			subtotalPrice,
			VATPrice,
			totalDue,
			period,
			period,
			isMakroReceipt,
			totalDueWords,
			vatDisplay,
		};

		// Location section
		const entitySection = buildEntitySection(solarEntity);

		// Main table section
		const mainTableSection = buildMainTableSection(tableParam);
		const inWordsTableSection = buildInWordsTableSection(totalDueWords);

		// PDF config
		const docDefinition = {
			pageSize: "A4",
			content: [
				{
					nodeName: "DIV",
					columns: [
						{
							nodeName: "DIV",
							width: "50%",
							stack: [
								{
									image: logo,
									width: 150,
								},
								...entitySection,
								{
									text: "TO",
									style: ["boldHeader"],
									margin: [0, 30, 0, 0],
								},
								{
									nodeName: "DIV",
									margin: [20, 7],
									stack: [
										{
											text: billingName,
											style: ["boldHeader"],
										},
										{
											text: billingAddress,
										},
										{
											text: `TAX ID : ${invoiceData.client_tax_id}`,
										},
									],
								},
							],
						},
						{
							nodeName: "DIV",
							width: "50%",
							stack: [
								// second column consists of paragraphs
								{ text: "Receipt", style: "header_lg" },
								{
									margin: [35, 0, 0, 0],
									table: {
										body: [
											[
												{
													text: "Receipt No.",
													style: ["boldHeader"],
												},
												{
													text: receiptData.receiptNo,
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "DATE",
													style: ["boldHeader"],
												},
												{
													text: paymentDate,
													style: ["invoice_details"],
												},
											],
											[
												{
													text: "INVOICE NO.",
													style: ["boldHeader"],
												},
												{
													text: receiptData.invoiceNo,
													style: ["invoice_details"],
												},
											],
										],
									},
									layout: {
										defaultBorder: false,
									},
								},
							],
						},
					],
				},
				{
					style: "table_main",
					table: mainTableSection,
					layout: {
						defaultBorder: false,
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 0;
						},
						paddingBottom: function (i, node) {
							return 0;
						},
					},
				},
				{
					table: inWordsTableSection,
					layout: {
						defaultBorder: false,
						paddingLeft: function (i, node) {
							return 0;
						},
						paddingRight: function (i, node) {
							return 0;
						},
						paddingTop: function (i, node) {
							return 0;
						},
						paddingBottom: function (i, node) {
							return 0;
						},
					},
				},
			],
			footer: [
				/* {
					columns: [
						{
							stack: [
								{
									text: "..............................................................................",
									style: "sub_signature",
								},
								{
									text: `Authorized by ${authName}`,
									style: "sub_signature",
								},
							],
							style: "signature",
						},
						{
							stack: [""],
							style: "signature",
						},
					],
					margin: [0, -135, 0, 0],
				}, */
				{
					stack: [
						{
							text: [
								"This is a computer-generated Invoice. No signature is required"
							],
						},
					],
					margin: [40, 20, 40, 0],
					style: {
						alignment: "center",
					},
				},
			],
			styles: {
				header_lg: {
					fontSize: 27,
					margin: [0, 0, 20, 15],
					alignment: "right",
				},
				sub_header: {
					decoration: "underline",
				},
				table_header: {
					margin: [0, 1, 0, 0],
					fontSize: 10,
				},
				table_main: {
					margin: [0, 30, 0, 0],
				},
				boldHeader: { bold: true },
				savings: {
					color: "green",
					bold: true,
				},
				numInWords: {
					fontSize: 7,
				},
				signature: {
					alignment: "center",
				},
				sub_signature: {
					margin: [0, 5, 0, 0],
				},
				thai: {
					font: "Trirong",
					fontSize: 8,
					margin: [0, 0, 0, -3],
				},
			},
			defaultStyle: {
				font: "Calibri",
				fontSize: 9,
			},
		};

		// Generate PDFMAKE document
		const pdfDocGenerator = pdfMake.createPdf(docDefinition);

		// Set PDFMAKE config for download in the later stage
		if (props.setPDFDoc != null) props.setPDFDoc(docDefinition);

		pdfDocGenerator.getDataUrl((dataUrl) => {
			setPDF(dataUrl);
		});
	};

	/**
	 * Build entity section object
	 * @param {Solar entity object} solarEntity
	 * @returns Entity section object
	 */
	const buildEntitySection = (solarEntity) => {
		const addressArr = solarEntity.address.split("_");
		const addressRows = addressArr.map((rows) => ({ text: rows }));

		const section = [
			{
				text: solarEntity.name,
				style: ["boldHeader"],
			},
			...addressRows,
			{
				text: `TAX ID : ${solarEntity.taxID}`,
			},
			{
				text: `Contact person : ${solarEntity.accountantName}`,
			},
			{
				text: `Tel: ${solarEntity.telephone}`,
			},
		];

		return section;
	};

	/**
	 * Build main table section
	 * @param {obj} data
	 * @returns Main table section
	 */
	const buildMainTableSection = (data) => {
		const isMakroReceipt = data.invoiceNo.includes("MKO");
		const showFTCost = data.totalPriceFT > 0;

		let bodyArr = [
			[
				{
					text: "DESCRIPTION",
					border: [false, true, false, true],
					style: ["table_header", "boldHeader"],
				},
				{
					border: [false, true, false, true],
					text: "QUANTITY",
					alignment: "right",
					style: ["table_header", "boldHeader"],
				},
				{
					border: [false, true, false, true],
					text: "UNIT COST (THB)",
					alignment: "right",
					style: ["table_header", "boldHeader"],
				},
				{
					border: [false, true, false, true],
					text: "TOTAL (THB)",
					alignment: "right",
					style: ["table_header", "boldHeader"],
				},
			],
		];

		const fullRows = [
			[
				{
					text: `\nPeriod: ${data.period}`,
					style: ["table_main_rows", "boldHeader"],
					colSpan: 4,
				},
			],
			[
				{
					text: `Electricity Production during On-PEAK Time ${data.discountON_display}`,
					style: "table_main_rows",
				},
				{
					text: data.solarON,
					style: "table_main_rows",
					alignment: "right",
				},
				{
					text: data.priceON_disc,
					style: "table_main_rows",
					alignment: "right",
				},
				{
					text: formatNumber(data.totalPriceON, 2),
					style: ["table_main_rows", "boldHeader"],
					alignment: "right",
				},
			],
			[
				{
					text: `Electricity Production during Off-PEAK Time ${data.discountOFF_display}`,
					style: "table_main_rows",
				},
				{
					text: data.solarOFF,
					alignment: "right",
					style: "table_main_rows",
				},
				{
					text: data.priceOFF_disc,
					alignment: "right",
					style: "table_main_rows",
				},
				{
					text: formatNumber(data.totalPriceOFF, 2),
					alignment: "right",
					style: ["table_main_rows", "boldHeader"],
				},
			],
			[
				{
					text: showFTCost ? `Ft Cost ${data.discountFT_display}` : "",
					style: "table_main_rows",
				},
				{
					text: showFTCost ? data.solarTotal : "",
					alignment: "right",
					style: "table_main_rows",
				},
				{
					text: showFTCost ? data.FTcost_disc : "",
					alignment: "right",
					style: "table_main_rows",
				},
				{
					text: showFTCost ? formatNumber(data.totalPriceFT, 2) : "",
					alignment: "right",
					style: ["table_main_rows", "boldHeader"],
				},
			],
			[
				{
					text: data.BPA_fee > 0 ? "Basic Power Availability Fee" : "",
					style: "table_main_rows",
					rowSpan: 2,
				},
				{
					text: data.BPA_fee > 0 ? "1.00" : "",
					alignment: "right",
					rowSpan: 2,
					style: "table_main_rows",
					padding: 0,
				},
				{
					text: data.BPA_fee > 0 ? formatNumber(data.BPA_fee, 2) : "",
					alignment: "right",
					rowSpan: 2,
					style: "table_main_rows",
				},
				{
					text: data.BPA_fee > 0 ? formatNumber(data.BPA_fee, 2) : "",
					alignment: "right",
					rowSpan: 2,
					style: ["table_main_rows", "boldHeader"],
				},
			],
			["EMPTY", "EMPTY", "EMPTY", "EMPTY"],
		];

		const makroRows = [
			["", "", "", ""],
			[
				{
					text: `\n${data.invoiceNo}`,
					style: "table_main_rows",
				},
				{
					text: "\n1.00",
					alignment: "right",
					style: "table_main_rows",
				},
				{
					text: `\n${formatNumber(data.subtotalPrice, 2)}`,
					alignment: "right",
					style: "table_main_rows",
				},
				{
					text: `\n${formatNumber(data.subtotalPrice, 2)}`,
					alignment: "right",
					style: ["table_main_rows", "boldHeader"],
				},
			],
		];

		if (isMakroReceipt) bodyArr = [...bodyArr, ...makroRows];
		else bodyArr = [...bodyArr, ...fullRows];

		return {
			body: [
				...bodyArr,
				[
					{
						colSpan: 3,
						text: "\n\n\n\n\n\nSubtotal",
						alignment: "right",
						style: ["table_main_rows", "boldHeader"],
					},
					"",
					"",
					{
						text: "\n\n\n\n\n\n" + formatNumber(data.subtotalPrice, 2),
						alignment: "right",
						style: ["table_main_rows", "boldHeader"],
					},
				],
				[
					{
						colSpan: 3,
						text: `VAT ${data.vatDisplay}%`,
						alignment: "right",
						style: ["table_main_rows", "boldHeader"],
					},
					"",
					"",
					{
						text: formatNumber(data.VATPrice, 2),
						alignment: "right",
						style: ["table_main_rows", "boldHeader"],
					},
				],
				[
					{
						colSpan: 3,
						text: data.specialDiscount_display,
						alignment: "right",
					},
					"",
					"",
					{
						text:
							data.discountPrice < 0 ? formatNumber(data.discountPrice, 2) : "",
						alignment: "right",
					},
				],
				[
					{
						colSpan: 3,
						text: "TOTAL DUE",
						alignment: "right",
						border: [false, true, false, true],
						style: "boldHeader",
						margin: [0, 1, 0, 0],
					},
					{ text: "", border: [false, true, false, true] },
					{ text: "", border: [false, true, false, true] },
					{
						text: formatNumber(data.totalDue, 2),
						alignment: "right",
						border: [false, true, false, true],
						style: "boldHeader",
						margin: [0, 1, 0, 0],
					},
				],
			],
			widths: ["55%", "15%", "15%", "15%"],
		};
	};

	/**
	 * Build total in words table
	 * @param {string} totalDueWords
	 * @returns Total in words section
	 */
	const buildInWordsTableSection = (totalDueWords) => {
		return {
			body: [
				[
					{
						text: "Total (In words)",
						border: [false, false, true, true],
						style: "boldHeader",
						margin: [0, 1, 0, 0],
					},
					{
						text: totalDueWords,
						style: "numInWords",
						border: [false, false, false, true],
						margin: [3, 1, 0, 0],
					},
				],
			],
			widths: ["20%", "80%"],
		};
	};

	/**
	 * Get discount text
	 */
	const getDiscountText = (discount) =>
		`- Discount ${formatNumber(discount * 100, 1)}%`;

	useEffect(() => {
		if (props.receipt != null) {
			generateReceipt(props.receipt)
		};

		return () => setPDF(null);
	}, [props.receipt]);

	return (
		<div>
			<iframe style={previewStyle} src={PDF} />
		</div>
	);
};

export default ReceiptTemplate;
